import { WDD } from '../core/wdd.js';

export function variants() {
    const product = WDD.el.querySelector('.bp-product');
    const panels = [...WDD.el.querySelectorAll('[data-summary-model]')];

    if (!product || !panels.length) return;

    const imageBase = WDD.el.querySelector('.bp-main')?.dataset.imageBase || 'https://image.alza.cz/Foto/or/bp/oppo/x10/img/';
    const supportsAvif = document.createElement('canvas').toDataURL('image/avif').startsWith('data:image/avif');
    const changingClass = 'bp-product__phones--changing';
    const revealingClass = 'bp-product__phones--revealing';
    const loadingClass = 'bp-product__phones--loading';
    const tokens = new Map();

    const panelFor = model => panels.find(panel => panel.dataset.summaryModel === model);
    const isBlocked = panel => product.classList.contains('bp-product--switching')
        || panel.querySelector('.jsBuy')?.classList.contains('bp-button--adding');

    function imageUrls(model, color, side) {
        const path = `${imageBase}variants/${model === 'pro-max' ? 'x10-pro-max' : 'x10'}-${color}-${side}`;
        const scale = window.devicePixelRatio >= 1.5 ? '@2x' : '';

        return {
            preload: `${path}${scale}.${supportsAvif ? 'avif' : 'png'}`,
            avif: `${path}.avif 1x, ${path}@2x.avif 2x`,
            png: `${path}.png 1x, ${path}@2x.png 2x`,
            fallback: `${path}.png`
        };
    }

    // Preloads the variant image and resolves once it is available in the browser cache
    function imageLoaded(url) {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(url);
            image.onerror = () => reject(new Error(`Unable to load image: ${url}`));
            image.src = url;
            if (image.complete && image.naturalWidth > 0) resolve(url);
        });
    }

    function setImage(image, model, color, choice) {
        const side = image.dataset.variantImage;
        const urls = imageUrls(model, color, side);
        const source = image.closest('picture')?.querySelector('[data-variant-source]');

        if (source) source.srcset = urls.avif;
        image.srcset = urls.png;
        image.src = urls.fallback;
        image.dataset.color = color;
        image.alt = side === 'front' ? choice.dataset.altFront : choice.dataset.altBack;
    }

    function select(panel, selector, attribute, value) {
        panel.querySelectorAll(selector).forEach(choice => {
            const selected = choice.dataset[attribute] === value;
            choice.classList.toggle('bp-button--active', selected);
            choice.classList.toggle('bp-button--secondary', !selected);
            choice.setAttribute('aria-pressed', String(selected));
        });
        panel.dispatchEvent(new CustomEvent('bp:variantchange', { bubbles: true }));
    }

    function cancelLoading(panel) {
        tokens.set(panel, (tokens.get(panel) || 0) + 1);
        panel.querySelector('.bp-product__phones')?.classList.remove(loadingClass, changingClass, revealingClass);
    }

    async function updateColor(panel, choice) {
        const model = panel.dataset.summaryModel;
        const color = choice.dataset.color;
        const phones = panel.querySelector('.bp-product__phones');
        const images = [...panel.querySelectorAll('[data-variant-image]')];
        const token = (tokens.get(panel) || 0) + 1;
        tokens.set(panel, token);
        phones.classList.add(loadingClass);

        try {
            await Promise.all(images.map(image => imageLoaded(imageUrls(model, color, image.dataset.variantImage).preload)));
            if (token !== tokens.get(panel)) return;

            phones.classList.replace(loadingClass, changingClass);
            await WDD.utils.transitionEnd(images[0], 'opacity', 200);
            if (token !== tokens.get(panel)) return;

            images.forEach(image => setImage(image, model, color, choice));
            select(panel, '[data-color-choice]', 'color', color);
            phones.classList.replace(changingClass, revealingClass);
            await WDD.utils.nextFrame();
            await WDD.utils.nextFrame();
            phones.classList.remove(revealingClass);
        } catch (error) {
            if (token !== tokens.get(panel)) return;
            phones.classList.remove(loadingClass, changingClass, revealingClass);
            console.error(`Unable to load the ${color} variant for ${model}:`, error);
        }
    }

    panels.forEach(panel => {
        panel.querySelectorAll('[data-color-choice]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.classList.contains('bp-button--active')));
            button.addEventListener('click', () => {
                if (button.disabled || button.classList.contains('bp-button--active') || isBlocked(panel)) return;
                void updateColor(panel, button);
            });
        });

        panel.querySelectorAll('[data-storage-choice]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.classList.contains('bp-button--active')));
            button.addEventListener('click', () => {
                if (button.disabled || button.classList.contains('bp-button--active') || isBlocked(panel)) return;
                select(panel, '[data-storage-choice]', 'storage', button.dataset.storage);
            });
        });
    });

    product.addEventListener('bp:modelchange', ({ detail: { previousModel } }) => {
        const panel = panelFor(previousModel);
        if (panel) cancelLoading(panel);
    });
}
